import { z } from 'zod';
import { BrandKindQuery, IdParams, IsoDateTime, KstDate, Rate } from '../common.ts';
import { ENGINES, ERROR_KINDS, OUTCOMES, SITE_KINDS } from '../probe-values.ts';
import { defineRoute } from '../route.ts';
import { Egress } from '../worker.ts';

const Days = z.object({ days: z.coerce.number().int().min(1).max(90).optional() });

/**
 * 창을 고르는 두 가지 — **최근 N일**(운영자 화면)과 **구독 회차**(고객 리포트).
 *
 * ★ **둘 다 보내면 `400`** 이다. "둘 다 오면 cycle 이 이긴다" 같은 **숨은 우선순위 규칙을 두지 않는다** —
 *   부르는 쪽이 자기가 무엇을 묻는지 모르는 상태를 서버가 조용히 메워 주면 **틀린 기간이 조용히 나간다.**
 * ★ `days` 를 **지우지 않는다.** 운영자 화면(`/admin`)은 "최근 7일"이 맞는 질문이고, 회차는
 *   고객 하나의 개념이라 질문마다 한 행인 운영자 표에 맞지 않는다.
 * ★ 화면은 **날짜를 보내지 않는다.** 경계는 서버가 그 브랜드의 **구독일**(`reportAnchorDay`)에서 정하므로
 *   `windowOf` 의 *"창은 여기 한 곳에서만 정한다"* 가 유지된다 (261001-client-works-report · 261015-subscription-unify).
 */
const Window = Days.extend({ cycle: z.coerce.number().int().min(0).max(120).optional() });

/**
 * 수집 엔진 — 없으면 `chatgpt-web` (261104-gemini-concurrent-lane). 운영자 화면의 「ChatGPT | Gemini」 탭이 보낸다.
 * ★ `chatgpt-web` 이 아닌 값은 **운영자만** — scoped 라우트에서 다른 역할이 보내면 403 이다(Gemini 는 어드민 전용).
 *   조용히 ChatGPT 로 바꿔 주지 않는다 — 부르는 쪽이 다른 엔진의 숫자를 받고도 모르게 된다
 */
export const EngineQuery = z.object({ engine: z.enum(ENGINES).optional() });

/**
 * 온보딩 쓰로틀링 상태 — **admin 에게만** 값이 있다 (260921-report-snapshot-and-onboarding-throttle).
 * `day`·`factor` 는 창의 **마지막 스냅샷** 기준이고, 스냅샷이 아직 없으면 null 이다.
 */
export const OnboardingState = z.object({
  enabled: z.boolean(),
  day: z.number().int().nullable(),
  /**
   * 그날 적용된 **온보딩 기간**(일). 질문마다 다르므로 화면은 상수가 아니라 이 값으로
   * `N/M일차` 를 그린다 (261001-onboarding-period-per-query).
   * ★ 질문의 **현재 설정이 아니라 일별 행에 확정된 값**이다 — 기간을 바꿔도 과거 행은 안 움직이는데
   *   분모만 현재 값으로 그리면 `14/120일차 ×0.577`(0.577 은 90일 기준) 처럼 서로 어긋난다
   */
  days: z.number().int().nullable(),
  factor: z.number().nullable(),
});
export type OnboardingState = z.infer<typeof OnboardingState>;

/** 쓰로틀링 전 실측 — **admin 에게만**. 고객 화면은 표시값만 본다 */
export const RawRates = z.object({
  mention: Rate,
  mentionInAnswer: Rate,
  mentionInPlaceCard: Rate,
  searchTriggered: Rate,
});
export type RawRates = z.infer<typeof RawRates>;

export const QueryMetricsRow = z.object({
  queryId: z.uuid(),
  query: z.string(),
  brandId: z.uuid(),
  brandName: z.string(),
  brandNames: z.array(z.string()),
  samplesPerDay: z.number().int(),
  attempts: z.number().int(),
  mention: Rate,
  mentionInAnswer: Rate,
  mentionInPlaceCard: Rate,
  searchTriggered: Rate,
  /** admin 에게만 값이 있다 — 우리 사이트 포트폴리오 노출 방지 (260915-access-roles) */
  ourSite: Rate.nullable(),
  /**
   * 인용 일치율 — 우리 사이트가 인용된 적격 샘플(n) 중 브랜드도 언급된 것(k). 실측이다(계수 밖).
   * **admin 에게만**. 분모는 `ourSite.k` 와 같거나 작다 — 이 값이 기록되기 전(2026-10-11) 스냅샷 날은 빠진다
   */
  ourSiteMention: Rate.nullable(),
  /**
   * 직전 같은 길이 창의 브랜드 언급률 — 전기 대비 증감을 재는 기준.
   * ★ 현재 창과 **같은 잣대(표시값)** 다. 표본이 모자라면 화면이 증감을 보이지 않는다
   */
  previous: Rate,
  /**
   * 최근 6일(어제까지)의 일별 브랜드 언급률 — 오래된 날부터. 스냅샷이 없는 날은 `n=0` 으로 채워 늘 6칸이다.
   * 화면은 최근 5일을 보이고, 맨 앞 하루는 5일 전 칸의 **전일 대비** 기준으로만 쓴다
   * ★ `mention` 은 헤드라인과 **같은 잣대(표시값)** 다. `raw` 는 쓰로틀링 전 실측이고 **admin 에게만**(toQueryRowView)
   */
  recentMention: z.array(z.object({ date: KstDate, mention: Rate, raw: Rate.nullable() })),
  /** 위 네 비율의 **쓰로틀링 전 실측**. admin 에게만 */
  raw: RawRates.nullable(),
  /** 온보딩 상태. admin 에게만 */
  onboarding: OnboardingState.nullable(),
  /** 오늘 수집 현황 — 스냅샷이 아니라 **실시간**이다(창의 끝은 어제다) */
  todayDone: z.number().int(),
  todayPlanned: z.number().int(),
  lastSampleAt: IsoDateTime.nullable(),
});
export type QueryMetricsRow = z.infer<typeof QueryMetricsRow>;

export const overview = defineRoute({
  method: 'get',
  path: '/api/overview',
  access: 'scoped',
  summary: '질문별 헤드라인 지표 (기본 7일)',
  /**
   * ★ `cycle` 을 보내면서 **고객을 특정하지 않으면 `400`** 이다(비-`brand` 역할). 회차는
   *   **고객 하나의 보고 기간**이라, 서버가 조용히 하나를 고르면 누구의 리포트인지 모르는 화면이 나온다.
   *   경계일이 지금은 전역 상수라 회차 자체는 고객 없이도 계산되지만, 구독 테이블이 생기면
   *   **고객마다 경계가 갈린다** — 그때 이 검사를 새로 만들지 않아도 되게 지금 건다.
   */
  /** `brandKind` 는 운영자의 합산(brandId 없음)에만 적용된다 */
  query: Window.extend({ brandId: z.uuid().optional() }).extend(BrandKindQuery.shape).extend(EngineQuery.shape),
  response: z.object({
    windowDays: z.number().int(),
    /** 리포트 기준일(= 어제). 스냅샷이 하나도 없으면 null */
    asOf: KstDate.nullable(),
    rows: z.array(QueryMetricsRow),
  }),
});

export const CitationDomain = z.object({
  host: z.string(),
  samples: z.number().int(),
  share: Rate,
  /** admin 에게만 값이 있다 */
  siteUrlPrefix: z.string().nullable(),
});

const DailyPoint = z.object({ date: KstDate, attempts: z.number().int(), mention: Rate });

/**
 * 운영자 질문 상세의 일별 점 — 표시값 옆에 **쓰로틀링 전 실측과 그날 계수**를 함께 싣는다
 * (260921-report-snapshot-and-onboarding-throttle "운영자 화면은 실측과 표시값을 나란히 본다").
 *
 * ★ **고객이 쓰는 `queryTrend` 는 base `DailyPoint` 를 그대로 쓴다.** 계약에 이 필드가 없으므로
 *   `mount` 의 `route.response.parse(out)`(http.ts)이 직렬화 전에 떨군다 — 같은 리포지터리 함수가
 *   실측을 실어 보내도 고객에게 닿을 길이 없다. 여기가 갈려 있는 것이 그 방어막이고,
 *   `queryTrend.daily` 를 이 스키마로 바꾸면 그 순간 사라진다.
 */
const AdminDailyPoint = DailyPoint.extend({
  /** 쓰로틀링 전 실측. **admin 에게만** — 이 라우트는 scoped 라 고객이 직접 부를 수 있다 (toDailyView) */
  raw: Rate.nullable(),
  /** 그날의 온보딩 계수. **admin 에게만**. 노이즈 때문에 날마다 달라 헤드라인의 계수 하나로는 알 수 없다 */
  factor: z.number().nullable(),
});
export type AdminDailyPoint = z.infer<typeof AdminDailyPoint>;

const Marker = z.object({ date: KstDate, kind: z.enum(['query_text', 'probe_version']), value: z.string() });

/**
 * 추이만 — **고객 화면이 쓰는 가벼운 쪽**이다 (260921-client-report-chart-only).
 * queryMetrics 는 인용 도메인 집계와 최근 샘플 20건을 **항상** 함께 계산해 싣는다. 고객 화면은 그 둘을
 * 쓰지 않는데, 첫 화면이 질문마다 그것을 받으면 안 쓰는 데이터가 질문 수만큼 브라우저로 가고
 * DB 왕복도 그만큼 는다. 그래서 갈랐다 — 운영자 화면은 계속 queryMetrics 를 쓴다.
 *
 * ★ `headline` 은 고객 화면이 그리지 않는다(수치는 overview 행에서 온다). 그래도 싣는 이유는 서버의
 *   `queryHeadline` 이 **범위 검사를 겸하기 때문**이다 — 남의 고객 질문이면 null 이라 404 로 닫힌다.
 *   빼면 그 검사를 따로 만들어야 한다. 빈 껍데기가 아니라 게이트다.
 *
 * ★ `daily` 는 **base `DailyPoint`** 다 — `AdminDailyPoint` 로 올리지 말 것. 실측·계수가 고객에게
 *   닿지 않는 이유가 역할 마스킹이 아니라 **이 스키마**이기 때문이다 (위 AdminDailyPoint 주석).
 */
export const queryTrend = defineRoute({
  method: 'get',
  path: '/api/queries/{id}/trend',
  access: 'scoped',
  summary: '질문 한 건의 헤드라인과 일별 추이 (인용 도메인·최근 샘플 없음)',
  params: IdParams,
  /** ★ 여기서는 `cycle` 에 `brandId` 를 요구하지 않는다 — **질문 id 가 이미 고객을 특정한다** */
  query: Window,
  response: z.object({
    windowDays: z.number().int(),
    asOf: KstDate.nullable(),
    headline: QueryMetricsRow,
    daily: z.array(DailyPoint),
    markers: z.array(Marker),
  }),
});

/** 샘플 목록의 한 행 — 질문 상세 「최근 샘플」과 전체 샘플 화면이 함께 쓴다 */
export const SampleRow = z.object({
  id: z.uuid(),
  createdAt: IsoDateTime,
  outcome: z.enum(OUTCOMES).nullable(),
  eligible: z.boolean(),
  mentioned: z.boolean(),
  citations: z.number().int(),
  entities: z.number().int(),
  /**
   * 인용 URL 중 우리 사이트(`geo_site`)가 있는가 — 헤드라인 「우리 사이트 인용률」과 같은 판정.
   * **admin 에게만** 값이 있다(toSampleRowView). 「언급」이 아니라 「인용」이다 — 우리 사이트는 URL 로만 잡힌다 (D-09)
   */
  ourSiteCited: z.boolean().nullable(),
});
export type SampleRow = z.infer<typeof SampleRow>;

export const queryMetrics = defineRoute({
  method: 'get',
  path: '/api/queries/{id}/metrics',
  access: 'scoped',
  summary: '질문 한 건의 추이·인용 도메인·최근 샘플',
  params: IdParams,
  query: Days.extend(EngineQuery.shape),
  response: z.object({
    windowDays: z.number().int(),
    /** 리포트 기준일(= 어제). 스냅샷이 하나도 없으면 null */
    asOf: KstDate.nullable(),
    headline: QueryMetricsRow,
    daily: z.array(AdminDailyPoint),
    markers: z.array(Marker),
    citationDomains: z.array(CitationDomain),
    recentSamples: z.array(SampleRow),
  }),
});

/** 질문 상세 「더보기」의 쪽 크기 — 서버가 정한다 */
export const QUERY_SAMPLES_PAGE_SIZE = 50;

/**
 * 질문 한 건의 **전체** 샘플을 최신순으로 쪽 단위로 — 운영자 질문 상세의 「더보기」.
 * ★ `admin` 이다. 화면이 운영자 영역에만 있고, scoped 로 열면 `ourSiteCited` 마스킹 면이 하나 더 생긴다
 */
export const querySamples = defineRoute({
  method: 'get',
  path: '/api/queries/{id}/samples',
  access: 'admin',
  summary: '질문 한 건의 전체 샘플 (최신순, 쪽 단위)',
  params: IdParams,
  query: z.object({ page: z.coerce.number().int().min(1).optional() }).extend(EngineQuery.shape),
  response: z.object({
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    samples: z.array(SampleRow),
  }),
});

export const SampleView = z.object({
  id: z.uuid(),
  queryId: z.uuid(),
  queryText: z.string(),
  brandNames: z.array(z.string()),
  createdAt: IsoDateTime,
  finishedAt: IsoDateTime.nullable(),
  outcome: z.enum(OUTCOMES).nullable(),
  errorKind: z.enum(ERROR_KINDS).nullable(),
  eligible: z.boolean(),
  answerText: z.string().nullable(),
  mention: z.object({ inAnswer: z.boolean(), inPlaceCard: z.boolean(), snippets: z.array(z.string()) }),
  citations: z.array(
    z.object({
      position: z.number().int(),
      url: z.string(),
      host: z.string(),
      title: z.string().nullable(),
      attribution: z.string().nullable(),
      siteUrlPrefix: z.string().nullable(),
    }),
  ),
  entities: z.array(
    z.object({
      position: z.number().int(),
      name: z.string(),
      websiteHost: z.string().nullable(),
      matched: z.boolean(),
    }),
  ),
  rawFiles: z.array(z.string()),
  /** admin 전용 — 비-admin 에게는 null (toView) */
  operator: z
    .object({
      workerId: z.uuid(),
      workerName: z.string(),
      egress: Egress.nullable(),
      acceptLanguage: z.string().nullable(),
      probeVersion: z.string().nullable(),
      chromeVersion: z.string().nullable(),
      generation: z.number().int(),
      diagnostics: z.record(z.string(), z.unknown()).nullable(),
    })
    .nullable(),
});
export type SampleView = z.infer<typeof SampleView>;

export const sample = defineRoute({
  method: 'get',
  path: '/api/samples/{id}',
  access: 'scoped',
  summary: '샘플 상세 — 답변 원문·매칭 구절·인용·장소 카드',
  params: IdParams,
  response: SampleView,
});

/** raw 파일은 바이너리 프록시라 zod 응답이 없다. 접근 수준은 scoped + 역할별 파일명 허용 목록 */
export const SAMPLE_RAW_PATH = '/api/samples/{id}/raw/{file}' as const;

export const citations = defineRoute({
  method: 'get',
  path: '/api/citations',
  access: 'admin',
  summary: '전체 인용 도메인 순위 (우리/외부)',
  query: Days.extend(BrandKindQuery.shape).extend(EngineQuery.shape),
  response: z.object({ windowDays: z.number().int(), eligible: z.number().int(), domains: z.array(CitationDomain) }),
});

export const siteMetrics = defineRoute({
  method: 'get',
  path: '/api/sites/metrics',
  access: 'admin',
  summary: '우리 사이트별 인용률',
  query: Days.extend(BrandKindQuery.shape).extend(EngineQuery.shape),
  response: z.object({
    windowDays: z.number().int(),
    eligible: z.number().int(),
    sites: z.array(
      z.object({
        id: z.uuid(),
        urlPrefix: z.string(),
        name: z.string(),
        kind: z.enum(SITE_KINDS).nullable(),
        outsideCf: z.boolean(),
        cited: Rate,
      }),
    ),
  }),
});

/**
 * 우리 사이트 하나가 **어느 질문에서** 인용됐는가 — `/admin/sites` 에서 사이트를 고르면 펼친다.
 * 창은 `siteMetrics` 와 같다(어제까지). 인용이 한 번이라도 있었던 질문만 싣는다.
 * ★ `admin` 이다 — 우리 사이트 포트폴리오는 고객·대행사에게 보이지 않는다 (260915-access-roles)
 */
export const siteQueries = defineRoute({
  method: 'get',
  path: '/api/sites/{id}/queries',
  access: 'admin',
  summary: '우리 사이트 하나가 인용된 질문들',
  params: IdParams,
  query: Days.extend(EngineQuery.shape),
  response: z.object({
    windowDays: z.number().int(),
    queries: z.array(
      z.object({
        queryId: z.uuid(),
        query: z.string(),
        brandName: z.string(),
        /** 질문이 삭제됐는가 — 과거 인용은 그대로 보인다 */
        deleted: z.boolean(),
        /** 그 질문의 적격 샘플(n) 중 이 사이트를 인용한 것(k) */
        cited: Rate,
        /** 이 사이트를 인용한 샘플 중 브랜드도 언급된 것 — 질문 단위 인용 일치 */
        citedWithMention: z.number().int(),
        lastCitedAt: IsoDateTime,
      }),
    ),
  }),
});

/**
 * 우리 사이트 하나를 인용한 **샘플 전부**(최신순) — `siteQueries` 를 묶지 않고 펼친 것이다.
 * 판정·창이 같으므로 질문별 `cited.k` 를 더하면 이 목록의 길이가 된다. 질문 필터는 화면이 한다.
 * ★ `admin` — 260915-access-roles
 */
export const siteSamples = defineRoute({
  method: 'get',
  path: '/api/sites/{id}/samples',
  access: 'admin',
  summary: '우리 사이트 하나를 인용한 샘플 전부 (최신순)',
  params: IdParams,
  query: Days.extend(EngineQuery.shape),
  response: z.object({
    windowDays: z.number().int(),
    samples: z.array(
      z.object({
        id: z.uuid(),
        createdAt: IsoDateTime,
        queryId: z.uuid(),
        query: z.string(),
        brandName: z.string(),
        mentioned: z.boolean(),
        /** 이 사이트에 매칭된 인용 URL — 인용 순서대로 */
        urls: z.array(z.string()),
      }),
    ),
  }),
});
